"use client";
import { useEffect, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { Logo } from "@/components/Logo";

/** Page-loading feedback. A tap on a same-origin link shows a thin amber bar
 *  along the top and the mark pulsing centre screen until the new page has
 *  rendered. A form submit shows the BAR ONLY — a form has its own busy state
 *  (a greyed button), and an overlay on top of it hid the very next step.
 *
 *  Buds, 20 Sep 2026, on the register page: "the logo is bouncing and screen
 *  is blocked." After "Email me a code" the page re-rendered in place with the
 *  code box and a button that stays DISABLED until six digits are typed; the
 *  first version read any disabled control as "still working" and sat there
 *  for its 20 s safety. Now the stop rule is about the form that was
 *  submitted, not the page: it stops when THAT form has left the document
 *  (the flow moved on) or its own submit button is enabled again (the action
 *  finished without moving). Safety at 6 s, never 20. */
type Busy = "nav" | "form" | null;

export function NavLoader() {
  const [busy, setBusy] = useState<Busy>(null);
  const pathname = usePathname();
  const search = useSearchParams();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const form = useRef<HTMLFormElement | null>(null);

  const stop = () => { setBusy(null); form.current = null; if (timer.current) clearTimeout(timer.current); };

  // Any route change means the new page is on screen — stop. Done during
  // render (the sanctioned derived-state adjustment), not in an effect.
  const [lastRoute, setLastRoute] = useState(pathname + "?" + search);
  if (lastRoute !== pathname + "?" + search) {
    setLastRoute(pathname + "?" + search);
    if (busy) setBusy(null);
  }

  useEffect(() => {
    const start = (kind: Exclude<Busy, null>) => {
      setBusy(kind);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(stop, 6_000); // safety: never stick
    };
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, location.href);
      if (url.origin !== location.origin) return;
      if (url.pathname === location.pathname && url.search === location.search) return; // same page (hash / no-op)
      start("nav");
    };
    const onSubmit = (e: Event) => {
      if (e.defaultPrevented) return;
      form.current = e.target as HTMLFormElement;
      start("form");
    };
    const onPageShow = () => stop();
    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  // A submitted form that finished without navigating: the flow moved on (the
  // form left the document) or its own button came back. Nothing else on the
  // page is evidence of anything.
  useEffect(() => {
    if (busy !== "form") { form.current = null; return; }
    const settled = () => {
      const f = form.current;
      if (!f || !document.contains(f)) return true;
      const btn = f.querySelector<HTMLButtonElement>('button[type="submit"], input[type="submit"]');
      return !btn || !btn.disabled;
    };
    const mo = new MutationObserver(() => { if (settled()) stop(); });
    mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["disabled", "aria-busy"] });
    return () => mo.disconnect();
  }, [busy]);

  if (!busy) return null;
  return (
    <div aria-live="polite" aria-label="Loading" className="pointer-events-none fixed inset-0 z-[100]">
      <div className="nav-bar absolute inset-x-0 top-0 h-[3px] bg-[#f5a31a]" />
      {busy === "nav" && (
        <div className="absolute inset-0 grid place-items-center bg-white/55 backdrop-blur-[1px]">
          <div className="nav-pulse flex items-center gap-2.5 rounded-2xl bg-white px-5 py-3 shadow-lg ring-1 ring-stone-200">
            <Logo size={28} />
            <span className="font-serif text-lg font-semibold text-stone-900">MakeMySiteToday</span>
          </div>
        </div>
      )}
    </div>
  );
}
